import { DragDropProvider } from '@dnd-kit/react';
import { isSortable } from '@dnd-kit/react/sortable';
import { toast } from 'react-toastify';
import type { ReactNode } from 'react';

import { STAGE_TONES, TICKET_STATUSES, TICKET_STATUS_LABELS } from '../../shared/index.js';
import type { StageTone } from '../../shared/index.js';

import type { BoardWithWorkflow } from '../../api/endpoints.js';
import { Select } from '../../components/select.js';
import { Badge, Button, CONTROL_FIELD, CONTROL_SELECT } from '../../components/ui.js';
import { TONE_DOT } from '../../components/ticket-card.js';
import type { StageDraft } from './board-setup-parts.js';
import { SortableStageRow } from './board-setup-parts.js';

type Stage = BoardWithWorkflow['workflow']['stages'][number];

interface MutationLike<TArgs> {
  readonly isPending: boolean;
  readonly mutate: (args: TArgs) => void;
}

interface ColumnsSectionProps {
  readonly boardId: string;
  readonly orderedStages: readonly Stage[];
  readonly allStages: readonly Stage[];
  readonly stageDraft: StageDraft;
  readonly onStageDraftChange: (next: StageDraft) => void;
  readonly onReorder: (stageIds: readonly string[]) => void;
  readonly addStage: MutationLike<string>;
  readonly editStage: MutationLike<{
    boardId: string;
    stageId: string;
    patch: Record<string, unknown>;
  }>;
  readonly dropStage: MutationLike<{ boardId: string; stageId: string }>;
}

export function ColumnsSection({
  boardId,
  orderedStages,
  allStages,
  stageDraft,
  onStageDraftChange,
  onReorder,
  addStage,
  editStage,
  dropStage,
}: ColumnsSectionProps): ReactNode {
  return (
    <section className="surface-card mb-5 p-5 dark:border-dark-line dark:bg-dark-panel">
      <h2 className="mb-1 text-[15px] font-bold text-navy dark:text-dark-ink">Columns</h2>
      <p className="mb-4 text-[12.5px] text-muted">
        Drag the handle on the left to change the order they appear in on the board.
      </p>

      <DragDropProvider
        onDragEnd={(event) => {
          const source = event.operation.source;
          const target = event.operation.target;

          if (event.canceled || source === null) return;

          if (target === null || target === undefined) return;

          const ids = orderedStages.map((entry) => entry.id);
          const from = ids.indexOf(String(source.id));

          if (from === -1) return;

          const moved = isSortable(source) ? source.index : -1;
          const to = moved !== -1 && moved !== from ? moved : ids.indexOf(String(target.id));

          if (to === -1 || to === from || to >= ids.length) return;

          const next = [...ids];
          next.splice(from, 1);
          next.splice(to, 0, String(source.id));

          onReorder(next);
        }}
      >
        <div className="mb-5 flex flex-col gap-2">
          {orderedStages.map((stage, index) => (
            <SortableStageRow key={stage.id} id={stage.id} index={index}>
              <span className={`size-2 rounded-full ${TONE_DOT[stage.tone]}`} />
              <input
                defaultValue={stage.label}
                onBlur={(event) => {
                  if (event.target.value.trim() === stage.label) return;
                  editStage.mutate({
                    boardId,
                    stageId: stage.id,
                    patch: { label: event.target.value.trim() },
                  });
                }}
                className="w-44 rounded-field border border-transparent bg-transparent px-2 py-1 text-[13.5px] font-semibold text-navy hover:border-hairline focus:border-royal/55 focus:bg-white focus:outline-none dark:text-dark-ink"
              />

              {stage.isInitial ? (
                <Badge tone="gold">Starting point</Badge>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    editStage.mutate({
                      boardId,
                      stageId: stage.id,
                      patch: { isInitial: true },
                    });
                  }}
                  className="text-[12px] font-semibold text-royal hover:underline"
                >
                  Make starting point
                </button>
              )}

              <Select
                label="Resulting status"
                value={stage.resultingStatus}
                onChange={(next) => {
                  editStage.mutate({
                    boardId,
                    stageId: stage.id,
                    patch: { resultingStatus: next },
                  });
                }}
                options={TICKET_STATUSES.map((status) => ({
                  value: status,
                  label: TICKET_STATUS_LABELS[status],
                }))}
                className="flex h-9 items-center gap-2 rounded-field border border-[rgb(223,228,235)] bg-white px-2 text-left text-[12.5px] font-semibold text-navy outline-none dark:border-dark-line dark:bg-dark-field dark:text-dark-ink"
              />

              <label
                className={`flex items-center gap-1.5 text-[12.5px] font-semibold ${
                  stage.isInitial ? 'cursor-not-allowed text-muted/50' : 'text-muted'
                }`}
                {...(stage.isInitial
                  ? { title: 'The starting column cannot also be an end state.' }
                  : {})}
                onClick={() => {
                  if (stage.isInitial) {
                    toast.info(
                      `"${stage.label}" is where new requests arrive, so it cannot also be an end state. Make another column the starting point first.`,
                    );
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={stage.isTerminal && !stage.isInitial}
                  disabled={stage.isInitial}
                  onChange={(event) => {
                    editStage.mutate({
                      boardId,
                      stageId: stage.id,
                      patch: { isTerminal: event.target.checked },
                    });
                  }}
                />
                End state
              </label>

              {stage.isInitial ? null : (
                <button
                  type="button"
                  onClick={() => {
                    dropStage.mutate({ boardId, stageId: stage.id });
                  }}
                  className="ml-auto text-[12.5px] font-semibold text-critical hover:underline"
                >
                  Delete
                </button>
              )}
            </SortableStageRow>
          ))}
        </div>
      </DragDropProvider>

      <div className="flex flex-wrap items-end gap-3 border-t border-hairline pt-4 dark:border-dark-line">
        <label className="block w-48">
          <span className="field-label">New column</span>
          <input
            value={stageDraft.label}
            onChange={(event) => {
              onStageDraftChange({ ...stageDraft, label: event.target.value });
            }}
            placeholder="Awaiting quote"
            className={CONTROL_FIELD}
          />
        </label>

        <label className="block w-32">
          <span className="field-label">Colour</span>
          <Select
            label="Colour"
            value={stageDraft.tone}
            onChange={(next) => {
              onStageDraftChange({ ...stageDraft, tone: next as StageTone });
            }}
            options={STAGE_TONES.map((tone) => ({ value: tone, label: tone }))}
            className={CONTROL_SELECT}
          />
        </label>

        <label className="block w-48">
          <span className="field-label">Place after</span>
          <Select
            label="Place after"
            value={stageDraft.afterStageId}
            onChange={(next) => {
              onStageDraftChange({ ...stageDraft, afterStageId: next });
            }}
            options={[
              { value: '', label: 'The end' },
              ...allStages.map((stage) => ({
                value: stage.id,
                label: stage.label,
              })),
            ]}
            className={CONTROL_SELECT}
          />
        </label>

        <Button
          size="sm"
          disabled={stageDraft.label.trim().length < 2}
          loading={addStage.isPending}
          onClick={() => {
            addStage.mutate(boardId);
          }}
        >
          Add column
        </Button>
      </div>
    </section>
  );
}
